import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';

var MONTHS_HE = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

function monthLabel(m) {
  if (!m) return '';
  var p = m.split('-');
  return (MONTHS_HE[parseInt(p[1], 10) - 1] || '') + ' ' + p[0];
}
function shortMonth(m) {
  if (!m) return '';
  var p = m.split('-');
  return (MONTHS_HE[parseInt(p[1], 10) - 1] || '').slice(0, 3) + " '" + p[0].slice(2);
}

function fmtValue(value, type) {
  var num = Number(value);
  if (value == null || value === '' || isNaN(num)) return '–';
  var s = num.toLocaleString('en-US');
  if (type === 'currency') return '₪' + s;
  if (type === 'percent') return s + '%';
  return s;
}

// Simple hand-rolled SVG bar chart of one metric across months (no chart lib —
// the project renders all charts as inline SVG, cf. KeywordChart.js).
function BarChart({ months, metricKey, metricType }) {
  var W = 720, H = 260, padL = 56, padR = 16, padT = 16, padB = 46;
  var data = months.map(function (mo) {
    var raw = mo.values ? mo.values[metricKey] : null;
    var num = Number(raw);
    return { month: mo.month, value: (raw == null || raw === '' || isNaN(num)) ? null : num };
  });
  var vals = data.filter(function (d) { return d.value != null; }).map(function (d) { return d.value; });
  if (vals.length === 0) {
    return React.createElement('div', { style: { padding: '30px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' } }, 'אין נתונים להצגה גרפית');
  }
  var maxV = Math.max.apply(null, vals);
  var chartW = W - padL - padR, chartH = H - padT - padB;
  var n = data.length;
  var slot = chartW / n;
  var barW = Math.min(48, slot * 0.6);

  function toY(v) { return padT + chartH - (maxV > 0 ? (v / maxV) * chartH : 0); }

  var gridLines = [];
  for (var i = 0; i <= 4; i++) {
    var gv = (maxV / 4) * i;
    var gy = toY(gv);
    gridLines.push(React.createElement('g', { key: 'g' + i },
      React.createElement('line', { x1: padL, y1: gy, x2: W - padR, y2: gy, stroke: 'var(--border)', strokeWidth: 1, strokeDasharray: '3 3' }),
      React.createElement('text', { x: padL - 8, y: gy + 4, textAnchor: 'end', fontSize: 10, fill: 'var(--text-muted)' }, fmtValue(Math.round(gv), metricType))
    ));
  }

  var bars = data.map(function (d, idx) {
    var cx = padL + slot * idx + slot / 2;
    var els = [React.createElement('text', { key: 'x' + idx, x: cx, y: H - padB + 16, textAnchor: 'middle', fontSize: 10, fill: 'var(--text-muted)' }, shortMonth(d.month))];
    if (d.value != null) {
      var by = toY(d.value);
      els.push(React.createElement('rect', { key: 'b' + idx, x: cx - barW / 2, y: by, width: barW, height: padT + chartH - by, rx: 4, fill: 'var(--accent)' }));
      els.push(React.createElement('text', { key: 't' + idx, x: cx, y: by - 5, textAnchor: 'middle', fontSize: 10, fontWeight: 600, fill: 'var(--text)' }, fmtValue(d.value, metricType)));
    }
    return React.createElement('g', { key: idx }, els);
  });

  return React.createElement('svg', { viewBox: '0 0 ' + W + ' ' + H, style: { width: '100%', height: 'auto' } },
    gridLines, bars
  );
}

export default function PaidAds() {
  var params = useParams();
  var id = params.id;
  var [site, setSite] = useState(null);
  var [platforms, setPlatforms] = useState([]);
  var [activePlatform, setActivePlatform] = useState(null);
  var [chartMetric, setChartMetric] = useState({}); // {platform: metricKey}
  var [loading, setLoading] = useState(true);

  var load = useCallback(function () {
    setLoading(true);
    Promise.all([
      api.getSite(id).catch(function () { return null; }),
      api.getPaidAdsSeries(id).catch(function () { return { platforms: [] }; }),
    ]).then(function (res) {
      setSite(res[0]);
      var pls = (res[1] && res[1].platforms) || [];
      setPlatforms(pls);
      if (pls.length) {
        setActivePlatform(pls[0].platform);
        var cm = {};
        pls.forEach(function (p) { cm[p.platform] = (p.metrics[0] || {}).key; });
        setChartMetric(cm);
      }
    }).finally(function () { setLoading(false); });
  }, [id]);

  useEffect(function () { load(); }, [load]);

  if (loading) {
    return React.createElement('div', { className: 'page-container' },
      React.createElement('div', { style: { padding: '40px', textAlign: 'center', color: 'var(--text-muted)' } }, 'טוען...'));
  }

  var active = platforms.find(function (p) { return p.platform === activePlatform; });

  return React.createElement('div', { className: 'page-container' },
    React.createElement('div', { className: 'page-header' },
      React.createElement('div', { className: 'page-title' }, 'תוצאות עסקיות'),
      React.createElement('div', { className: 'page-subtitle' }, site ? site.name : '')
    ),

    platforms.length === 0
      ? React.createElement('div', { className: 'section-card', style: { padding: '40px', textAlign: 'center', color: 'var(--text-muted)' } },
          'אין נתונים עדיין. יש להגדיר ערוצי פרסום או קבוצות תוצאות בהגדרות האתר, ולהזין נתונים במסך הדוחות.')
      : React.createElement('div', null,
          // Platform tabs
          React.createElement('div', { style: { display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' } },
            platforms.map(function (p) {
              return React.createElement('button', {
                key: p.platform,
                className: p.platform === activePlatform ? 'btn btn-primary' : 'btn btn-secondary',
                onClick: function () { setActivePlatform(p.platform); },
                style: { padding: '6px 14px', fontSize: '13px' },
              }, p.label);
            })
          ),

          active && React.createElement('div', null,
            // Chart card
            React.createElement('div', { className: 'section-card', style: { padding: '20px', marginBottom: '16px' } },
              React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' } },
                React.createElement('div', { style: { fontWeight: 700, fontSize: '16px' } }, 'גרף חודשי'),
                React.createElement('select', {
                  value: chartMetric[active.platform] || '',
                  onChange: function (e) { setChartMetric(Object.assign({}, chartMetric, (function () { var o = {}; o[active.platform] = e.target.value; return o; })())); },
                  style: { padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: '13px' },
                }, active.metrics.map(function (m) { return React.createElement('option', { key: m.key, value: m.key }, m.label); }))
              ),
              React.createElement(BarChart, {
                months: active.months,
                metricKey: chartMetric[active.platform],
                metricType: (active.metrics.find(function (m) { return m.key === chartMetric[active.platform]; }) || {}).type,
              })
            ),

            // Table card — Excel style: rows = metrics, columns = months (newest first)
            (function () {
              var monthsDesc = active.months.slice().sort(function (a, b) { return a.month < b.month ? 1 : -1; });
              var valueOf = function (mo, key) { return mo.values ? mo.values[key] : null; };
              return React.createElement('div', { className: 'section-card', style: { overflowX: 'auto' } },
                React.createElement('div', { className: 'section-header', style: { padding: '16px 20px' } },
                  React.createElement('div', { className: 'section-title' }, 'טבלת נתונים חודשית')
                ),
                React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', minWidth: '600px' } },
                  React.createElement('thead', null,
                    React.createElement('tr', { style: { borderBottom: '1px solid var(--border)' } },
                      [React.createElement('th', { key: 'h-metric', style: thStyle }, 'מדד')].concat(
                        monthsDesc.map(function (mo) { return React.createElement('th', { key: 'h-' + mo.month, style: Object.assign({}, thStyle, { direction: 'ltr' }) }, monthLabel(mo.month)); })
                      )
                    )
                  ),
                  React.createElement('tbody', null,
                    active.metrics.map(function (m) {
                      return React.createElement('tr', { key: m.key, style: { borderBottom: '1px solid var(--border)' } },
                        [React.createElement('td', { key: 'label', style: Object.assign({}, tdStyle, { fontWeight: 600 }) }, m.label)].concat(
                          monthsDesc.map(function (mo) {
                            return React.createElement('td', { key: mo.month, style: Object.assign({}, tdStyle, { direction: 'ltr', textAlign: 'right' }) }, fmtValue(valueOf(mo, m.key), m.type));
                          })
                        )
                      );
                    })
                  )
                )
              );
            })()
          )
        )
  );
}

var thStyle = { padding: '10px 16px', textAlign: 'right', fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600, whiteSpace: 'nowrap' };
var tdStyle = { padding: '10px 16px', fontSize: '13px', color: 'var(--text)', whiteSpace: 'nowrap' };
